import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Calendar, Clock, ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SEO from "../components/SEO";
import SectionReveal from "../components/SectionReveal";
import { publicApi } from "../lib/adminApi";
import { trackEvent } from "../lib/analytics";
import NotFound from "./NotFound";

const SITE_URL = "https://ridgehawk.ca";

export default function BlogPost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setNotFound(false); setData(null);
    (async () => {
      const res = await publicApi.getBlog(slug);
      if (cancelled) return;
      if (res.ok) setData(res.data);
      else if (res.status === 404) setNotFound(true);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) return <p className="container-pad py-32 text-sm text-ridge-muted">Loading…</p>;
  if (notFound || !data) return <NotFound />;

  const { post, related } = data;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt || post.seo_description,
    image: post.cover_image_url ? `${SITE_URL}${post.cover_image_url.startsWith("http") ? "" : ""}${post.cover_image_url}` : `${SITE_URL}/logo.jpeg`,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    author: { "@type": "Person", name: post.author_name || "Ridgehawk Logistics" },
    publisher: { "@type": "Organization", name: "Ridgehawk Logistics", logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.jpeg` } },
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
      { "@type": "ListItem", position: 3, name: post.title, item: `${SITE_URL}/blog/${post.slug}` },
    ],
  };

  return (
    <>
      <SEO title={post.seo_title || post.title} description={post.seo_description || post.excerpt} path={`/blog/${post.slug}`} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Cover */}
      {post.cover_image_url ? (
        <div className="relative h-[42vh] min-h-[360px] w-full overflow-hidden bg-ridge-charcoal" data-testid="blog-post-cover">
          <img src={post.cover_image_url} alt={post.cover_image_alt || ""} className="h-full w-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-ridge-charcoal/60 to-transparent" />
        </div>
      ) : (
        <div className="h-16 bg-ridge-charcoal" />
      )}

      <article className="bg-white">
        <div className="container-pad py-12 md:py-16 lg:py-20">
          <SectionReveal>
            <div className="mx-auto max-w-[760px]">
              <Link to="/blog" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-ridge-amber hover:text-ridge-amber-dark">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to all posts
              </Link>
              <h1 className="font-display mt-5 text-4xl font-bold leading-[1.08] tracking-tight text-ridge-charcoal sm:text-5xl">
                {post.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-4 text-xs font-semibold uppercase tracking-[0.16em] text-ridge-muted">
                {post.author_name ? (
                  <span><span className="text-ridge-charcoal">{post.author_name}</span>{post.author_role ? ` · ${post.author_role}` : ""}</span>
                ) : null}
                {post.published_at ? (
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{(post.published_at || "").slice(0, 10)}</span>
                ) : null}
                {post.reading_time_minutes ? (
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{post.reading_time_minutes} min read</span>
                ) : null}
              </div>
              {(post.tags || []).length ? (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {post.tags.map((t) => (
                    <span key={t} className="rounded bg-ridge-stone px-2.5 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-ridge-charcoal">{t}</span>
                  ))}
                </div>
              ) : null}
            </div>
          </SectionReveal>

          <div className="mx-auto mt-10 max-w-[720px]" data-testid="blog-post-body">
            <div className="prose-ridge text-base leading-[1.85] text-ridge-text md:text-lg">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body || ""}</ReactMarkdown>
            </div>
          </div>
        </div>
      </article>

      {/* CTA */}
      <section className="bg-ridge-amber" data-testid="blog-post-cta">
        <div className="container-pad py-14 md:py-20">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
            <h2 className="font-display max-w-2xl text-2xl font-bold tracking-tight text-white md:text-3xl">
              Need precision logistics for your next move?
            </h2>
            <Link to="/quote" className="inline-flex items-center justify-center gap-2 rounded-md bg-ridge-charcoal px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.08em] text-white hover:bg-black">
              Get a Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {related?.length ? (
        <section className="section-pad bg-white" data-testid="blog-related-section">
          <div className="container-pad">
            <h2 className="font-display text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">More insights</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="group block overflow-hidden rounded-md border border-ridge-border bg-white transition-colors hover:border-ridge-charcoal/25">
                  <div className="aspect-[16/10] w-full overflow-hidden bg-ridge-stone">
                    {r.cover_image_url ? <img src={r.cover_image_url} alt={r.cover_image_alt || ""} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-ridge-charcoal to-ridge-amber-dark" />}
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-base font-semibold leading-snug text-ridge-charcoal group-hover:text-ridge-amber-dark">{r.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}
